import React, { useState, useEffect } from 'react';
import { API } from '../utils/api';
import './SessionManager.css';

interface SessionManagerProps {
  onSessionSelect: (sessionId: string, role: 'DM' | 'Player') => void;
}

const SessionManager: React.FC<SessionManagerProps> = ({ onSessionSelect }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableSessions, setAvailableSessions] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [roleSelectSession, setRoleSelectSession] = useState<string | null>(null);

  // Delete session state
  const [deletePassword, setDeletePassword] = useState('');

  // Create session state
  const [createSessionId, setCreateSessionId] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [sessionDescription, setSessionDescription] = useState('');
  const [startYear, setStartYear] = useState(1048);
  const [startMonth, setStartMonth] = useState(0);

  const monthNames = ["Auro'ithil","Man'alasse","Thael'orne","Pel'anor","Drac'uial","Val'kaurn","Shad'morn","Ley'thurin","Nex'illien","Tun'giliath","Mor'galad","Cir'annen"];

  // Load sessions on mount and try auto-select
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await API.sessions.getAll();
      if (response.ok) {
        const sessions = await response.json();
        setAvailableSessions(sessions);

        // Auto-select: check localStorage for last session
        const lastSessionId = localStorage.getItem('last-session-id');
        const lastRole = localStorage.getItem(`session-role-${lastSessionId}`) as 'DM' | 'Player' | null;

        if (lastSessionId && lastRole) {
          const sessionExists = sessions.some((s: any) => s.id === lastSessionId);
          if (sessionExists) {
            onSessionSelect(lastSessionId, lastRole);
            return;
          }
        }

        // If only one session exists, show role selection for it
        if (sessions.length === 1) {
          setRoleSelectSession(sessions[0].id);
        }
      } else {
        setError('Fehler beim Laden der Sessions');
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Verbindungsfehler');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinWithRole = (sessionId: string, role: 'DM' | 'Player') => {
    localStorage.setItem('last-session-id', sessionId);
    localStorage.setItem(`session-role-${sessionId}`, role);
    onSessionSelect(sessionId, role);
  };

  const createSession = async () => {
    if (!createSessionId.trim() || !sessionName.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const checkResponse = await API.sessions.exists(createSessionId.trim());

      if (checkResponse.ok) {
        const data = await checkResponse.json();
        if (data.exists) {
          setError('Eine Session mit dieser ID existiert bereits.');
          setIsLoading(false);
          return;
        }
      }

      const response = await API.sessions.create({
        id: createSessionId.trim(),
        name: sessionName.trim(),
        description: sessionDescription.trim(),
        startYear: startYear,
        startMonth: startMonth
      });

      if (response.ok) {
        handleJoinWithRole(createSessionId.trim(), 'DM');
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Erstellen der Session');
      }
    } catch (err) {
      console.error('Failed to create session:', err);
      setError('Verbindungsfehler');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!deletePassword.trim()) {
      setError('Passwort erforderlich');
      return;
    }

    const confirmed = window.confirm(`Session "${sessionId}" wirklich löschen? Alle Daten gehen verloren!`);
    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await API.sessions.delete(sessionId, deletePassword.trim());

      if (response.ok) {
        setDeletePassword('');
        localStorage.removeItem('last-session-id');
        localStorage.removeItem(`session-role-${sessionId}`);
        loadSessions();
      } else {
        const data = await response.json();
        setError(data.error || 'Fehler beim Löschen');
      }
    } catch (err) {
      setError('Verbindungsfehler');
    } finally {
      setIsLoading(false);
    }
  };

  // Role selection overlay
  if (roleSelectSession) {
    const session = availableSessions.find(s => s.id === roleSelectSession);
    return (
      <div className="session-manager">
        <div className="session-welcome">
          <h2>Aetherial Calendar</h2>
          <p className="session-join-name">{session?.name || roleSelectSession}</p>
          <p>Wie möchtest du beitreten?</p>
        </div>

        <div className="role-selection">
          <button
            className="role-button role-dm"
            onClick={() => handleJoinWithRole(roleSelectSession, 'DM')}
          >
            <h3>Dungeon Master</h3>
            <p>Events erstellen, Wetter setzen, Tage verwalten</p>
          </button>
          <button
            className="role-button role-player"
            onClick={() => handleJoinWithRole(roleSelectSession, 'Player')}
          >
            <h3>Spieler</h3>
            <p>Kalender ansehen, Notizen erstellen</p>
          </button>
        </div>

        {availableSessions.length > 1 && (
          <button
            className="back-link"
            onClick={() => setRoleSelectSession(null)}
          >
            Andere Session wählen
          </button>
        )}
      </div>
    );
  }

  // Loading state
  if (isLoading && availableSessions.length === 0) {
    return (
      <div className="session-manager">
        <div className="session-welcome">
          <h2>Aetherial Calendar</h2>
          <p>Sessions werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="session-manager">
      <div className="session-welcome">
        <h2>Aetherial Calendar</h2>
        {availableSessions.length > 0 ? (
          <p>Wähle eine Session</p>
        ) : (
          <p>Erstelle deine erste Session um zu beginnen</p>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Session list */}
      {availableSessions.length > 0 && (
        <div className="sessions-grid">
          {availableSessions.map((session: any) => (
            <div
              key={session.id}
              className="session-card"
              onClick={() => setRoleSelectSession(session.id)}
            >
              <div className="session-card-main">
                <h3>{session.name}</h3>
                {session.description && (
                  <p className="session-description">{session.description}</p>
                )}
                <div className="session-meta">
                  <span>Start: {monthNames[session.start_month]} {session.start_year}</span>
                  <span className="session-id-display">ID: {session.id}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create new session */}
      {!showCreateForm ? (
        <button
          className="create-toggle-button"
          onClick={() => setShowCreateForm(true)}
        >
          + Neue Session erstellen
        </button>
      ) : (
        <div className="create-form-container">
          <div className="create-form-header">
            <h3>Neue Session</h3>
            <button className="close-button" onClick={() => {
              setShowCreateForm(false);
              setError(null);
            }}>
              ×
            </button>
          </div>

          <div className="session-form">
            <div className="input-group">
              <label>Session ID *</label>
              <input
                type="text"
                placeholder="z.B. 'die-ueberlebenden'"
                value={createSessionId}
                onChange={(e) => setCreateSessionId(e.target.value)}
                disabled={isLoading}
                autoFocus
              />
              <small>Eindeutige ID (keine Leerzeichen)</small>
            </div>

            <div className="input-group">
              <label>Name *</label>
              <input
                type="text"
                placeholder="z.B. 'Die Überlebenden von Aetheria'"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="input-group">
              <label>Beschreibung</label>
              <textarea
                placeholder="Kurze Beschreibung (optional)"
                value={sessionDescription}
                onChange={(e) => setSessionDescription(e.target.value)}
                disabled={isLoading}
                rows={2}
              />
            </div>

            <div className="date-controls">
              <div className="input-group">
                <label>Startjahr</label>
                <input
                  type="number"
                  min="1"
                  max="9999"
                  value={startYear}
                  onChange={(e) => setStartYear(parseInt(e.target.value) || 1048)}
                  disabled={isLoading}
                />
              </div>

              <div className="input-group">
                <label>Startmonat</label>
                <select
                  value={startMonth}
                  onChange={(e) => setStartMonth(parseInt(e.target.value))}
                  disabled={isLoading}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="cancel-button"
                onClick={() => setShowCreateForm(false)}
              >
                Abbrechen
              </button>
              <button
                className="create-session-button"
                onClick={createSession}
                disabled={!createSessionId.trim() || !sessionName.trim() || isLoading}
              >
                {isLoading ? 'Erstelle...' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete section - only show if sessions exist */}
      {availableSessions.length > 0 && (
        <div className="delete-section">
          <details>
            <summary>Session löschen</summary>
            <div className="delete-controls">
              <input
                type="password"
                placeholder="Lösch-Passwort"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="delete-password-input"
              />
              <div className="delete-buttons">
                {availableSessions.map((session: any) => (
                  <button
                    key={session.id}
                    className="delete-session-button"
                    onClick={() => deleteSession(session.id)}
                    disabled={!deletePassword.trim() || isLoading}
                  >
                    {session.name} löschen
                  </button>
                ))}
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default SessionManager;
